import { userErrorMessage } from '@wx-weiqi/contracts';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
  CameraIcon,
  EnterFullScreenIcon,
  ExitFullScreenIcon,
  ListBulletIcon,
  Pencil2Icon,
  ResetIcon,
  ZoomInIcon,
  ZoomOutIcon,
  TargetIcon,
  LightningBoltIcon,
  GearIcon,
  MagicWandIcon,
} from '@radix-ui/react-icons';
import { HomeworkMoveControls } from './HomeworkMoveControls';
import { RecentReviewTimingChart, ReviewPlayerStone, ReviewTiming } from './ReviewTiming';
import { formatPreciseDuration } from '../domain/timing-report';
import { TeacherObservationTrendChart } from './TeacherObservationTrendChart';
import type {
  BoardPoint,
  GameHistoryItem,
  GameReviewFrame,
  StoneColor,
  TeacherGameSummary,
} from '@wx-weiqi/contracts';

import { pointKey } from '../domain/board-geometry';
import {
  recentReviewTimingFrames,
  reviewResearchStoneLabels,
  type ReviewMarkerMode,
  summarizeReviewTiming,
} from '../domain/game-review-presentation';
import { useObservationTimeline } from './use-observation-timeline';
import { formatReviewFrameRemainingTime } from '../domain/review-clock';
import { boardViewportForCorner, type HomeworkBoardCorner } from '../domain/homework-board-focus';
import {
  branchResearchVariation,
  researchStoneLabels,
  researchVariationPrefix,
} from '../domain/fox-kifu-presentation';
import {
  ZHIZI_COMPUTE_MODE_LABELS,
  zhiziWeightLabel,
  ZHIZI_EXCLUSIVE_TIER_LABELS,
} from '../domain/zhizi-profile';
import { observationPositionKey, type GameReplayStore } from '../stores/game-replay-store';
import { BoardSurface } from './BoardSurface';
import { BoardMoveProgress } from './BoardMoveProgress';
import { AiCandidatePanel } from './AiCandidatePanel';
import { ClassroomAnnotationToolbar } from './ClassroomAnnotationToolbar';
import { AiAccountSelector } from './AiAccountSelector';
import { selectedAccount } from '../stores/ai-account-selection';
import { aiVariationStoneLabels, presentAiCandidates } from '../domain/ai-candidate-presentation';
import {
  ReviewAnnotationLayer,
  type ReviewAnnotation,
  type ReviewAnnotationColor,
  type ReviewAnnotationTool,
  type ReviewBrushStroke,
  toggleReviewPointAnnotation,
} from './ReviewAnnotationLayer';
import { canStartReviewPanelDrag } from './ReviewAnnotationPanel';
import { downloadBoardJpeg, foxBoardCaptureFilename } from '../services/board-capture';
import { PlayerGamePickerDialog } from './PlayerGamePickerDialog';
import { TeacherGamePickerDialog, type TeacherGamePickerScope } from './TeacherGamePickerDialog';
import { VerticalMoveSlider } from './VerticalMoveSlider';
import { useBoardWheelNavigation } from './useBoardWheelNavigation';

type MarkerMode = ReviewMarkerMode;

const savedAnalysisDate = new Intl.DateTimeFormat('zh-CN', {
  timeZone: 'Asia/Shanghai',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const MARKER_LABELS: Readonly<Record<MarkerMode, string>> = {
  LAST_ONE: '最后1手',
  LAST_FIVE: '最后5手',
  ALL: '全部手数',
};

const BOARD_CORNER_LABELS: Readonly<Record<HomeworkBoardCorner, string>> = {
  TOP_LEFT: '左上角',
  TOP_RIGHT: '右上角',
  BOTTOM_LEFT: '左下角',
  BOTTOM_RIGHT: '右下角',
};

const EMPTY_BOARD = Object.freeze(Array.from({ length: 361 }, () => null));

export function reviewFloatingPanelLayout(aiEnabled: boolean, annotationOpen: boolean) {
  return Object.freeze({
    showAiPanel: aiEnabled,
    showAnnotationPanel: annotationOpen,
    annotationSharesStage: aiEnabled && annotationOpen,
  });
}

export function reviewBoardZoomButtonAction(
  boardCorner: HomeworkBoardCorner | null,
): 'RESTORE_FULL_BOARD' | 'TOGGLE_CORNER_MENU' {
  return boardCorner === null ? 'TOGGLE_CORNER_MENU' : 'RESTORE_FULL_BOARD';
}

export function gameReviewRecentTimingLimit(): number {
  return 5;
}

export { canStartReviewPanelDrag };

interface GameHistoryReviewPageProps {
  readonly store: GameReplayStore;
  readonly title: string;
  readonly reportPending: boolean;
  readonly teacherToolsEnabled: boolean;
  readonly playerGames: readonly GameHistoryItem[];
  readonly playerGameListPending: boolean;
  readonly playerGameListError: string | null;
  readonly teacherGames: readonly TeacherGameSummary[];
  readonly teacherGameListTitle: string;
  readonly teacherGameListScope: TeacherGamePickerScope;
  readonly teacherGameListPending: boolean;
  readonly teacherGameListError: string | null;
  readonly onBack: () => void;
  readonly onRefreshPlayerGames: () => Promise<void>;
  readonly onRefreshTeacherGames: () => Promise<void>;
  readonly onSelectGame: (gameId: string) => Promise<void>;
}

function labeledMoves(
  frames: readonly GameReviewFrame[],
  moveNumber: number,
  markerMode: MarkerMode,
) {
  const candidates = frames
    .slice(1, moveNumber + 1)
    .filter(
      (
        frame,
      ): frame is GameReviewFrame & { readonly point: NonNullable<GameReviewFrame['point']> } =>
        frame.kind === 'PLAY' && frame.point !== null && frame.color !== null,
    );
  const visible =
    markerMode === 'ALL' ? candidates : candidates.slice(markerMode === 'LAST_ONE' ? -1 : -5);
  const latest = new Map<string, (typeof visible)[number]>();
  visible.forEach((frame) => latest.set(pointKey(frame.point), frame));
  return [...latest.values()].map((frame) => ({
    point: frame.point,
    label: String(frame.moveNumber),
    color: frame.color!,
  }));
}

export function GameHistoryReviewPage({
  store,
  title,
  reportPending,
  teacherToolsEnabled,
  playerGames,
  playerGameListPending,
  playerGameListError,
  teacherGames,
  teacherGameListTitle,
  teacherGameListScope,
  teacherGameListPending,
  teacherGameListError,
  onBack,
  onRefreshPlayerGames,
  onRefreshTeacherGames,
  onSelectGame,
}: GameHistoryReviewPageProps) {
  const state = useSyncExternalStore(store.subscribe, store.snapshot);
  const [moveNumber, setMoveNumber] = useState(0);
  const [markerMode, setMarkerMode] = useState<MarkerMode>('LAST_ONE');
  const [immersive, setImmersive] = useState(false);
  const [researchBase, setResearchBase] = useState<number | null>(null);
  const [variationMoves, setVariationMoves] = useState<readonly BoardPoint[]>([]);
  const [researchCursor, setResearchCursor] = useState(0);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiRecommendationsOpen, setAiRecommendationsOpen] = useState(false);
  const [aiConnectionOpen, setAiConnectionOpen] = useState(false);
  const [aiChecking, setAiChecking] = useState(false);
  const [annotationOpen, setAnnotationOpen] = useState(false);
  const [annotationTool, setAnnotationTool] = useState<ReviewAnnotationTool>('BRUSH');
  const [annotationColor, setAnnotationColor] = useState<ReviewAnnotationColor>('RED');
  const [annotationWidth, setAnnotationWidth] = useState(7);
  const [annotations, setAnnotations] = useState<readonly ReviewAnnotation[]>([]);
  const [gameListOpen, setGameListOpen] = useState(false);
  const [boardZoomMenuOpen, setBoardZoomMenuOpen] = useState(false);
  const [boardCorner, setBoardCorner] = useState<HomeworkBoardCorner | null>(null);
  const [captureMessage, setCaptureMessage] = useState<string | null>(null);
  const [analyticsTab, setAnalyticsTab] = useState<'TIMING' | 'WIN_RATE'>('WIN_RATE');
  const pageRef = useRef<HTMLElement | null>(null);
  const boardStageRef = useRef<HTMLDivElement | null>(null);
  const review = state.review;
  const frames = review?.frames ?? [];
  const totalMoves = Math.max(0, frames.length - 1);
  const frame = frames[Math.min(moveNumber, totalMoves)];
  const boardSize = review?.record.boardSize ?? 19;
  const researching = researchBase !== null;
  const activeVariationMoves = useMemo(
    () => researchVariationPrefix(variationMoves, researchCursor),
    [researchCursor, variationMoves],
  );
  const stones = researching
    ? (state.research?.stones ?? frame?.stones ?? EMPTY_BOARD)
    : (frame?.stones ?? EMPTY_BOARD);
  const labels = useMemo(() => {
    if (researching && activeVariationMoves.length > 0) {
      return teacherToolsEnabled
        ? reviewResearchStoneLabels(activeVariationMoves, stones, boardSize, markerMode)
        : researchStoneLabels(activeVariationMoves, stones, boardSize);
    }
    return labeledMoves(frames, moveNumber, markerMode);
  }, [
    activeVariationMoves,
    boardSize,
    frames,
    markerMode,
    moveNumber,
    researching,
    stones,
    teacherToolsEnabled,
  ]);
  const visibleTiming = useMemo(
    () => summarizeReviewTiming(frames, moveNumber),
    [frames, moveNumber],
  );
  const fullTiming = useMemo(() => summarizeReviewTiming(frames, totalMoves), [frames, totalMoves]);
  const recentTimedMoves = recentReviewTimingFrames(
    frames,
    moveNumber,
    gameReviewRecentTimingLimit(),
  );
  const rulesAvailable =
    review?.record.ruleset !== undefined &&
    review.record.komi !== undefined &&
    review.record.handicap !== undefined &&
    review.record.currentPlayer !== undefined;
  const reviewAnalysisPosition = useMemo(
    () =>
      review !== null && state.reviewStateVersion !== null && rulesAvailable
        ? {
            gameId: review.record.gameId,
            stateVersion: state.reviewStateVersion,
            baseMoveNumber: researchBase ?? moveNumber,
            moves: researching ? activeVariationMoves : [],
          }
        : null,
    [
      review,
      state.reviewStateVersion,
      rulesAvailable,
      researchBase,
      moveNumber,
      researching,
      activeVariationMoves,
    ],
  );
  const matchingAnalysis = !aiEnabled
    ? null
    : !teacherToolsEnabled
      ? state.aiAnalysis
      : reviewAnalysisPosition !== null &&
          state.observationAiPosition !== null &&
          observationPositionKey(reviewAnalysisPosition) ===
            observationPositionKey(state.observationAiPosition)
        ? state.aiAnalysis
        : null;
  const timeline = useObservationTimeline(
    store,
    teacherToolsEnabled && review !== null && state.reviewStateVersion !== null && rulesAvailable
      ? {
          gameId: review.record.gameId,
          stateVersion: state.reviewStateVersion,
          frames,
          configurationKey: JSON.stringify([state.aiSelectedConfigurationId, state.aiProfile]),
          rulesKey: JSON.stringify([
            boardSize,
            review.record.ruleset,
            review.record.komi,
            review.record.handicap,
          ]),
          currentPlayer: review.record.currentPlayer!,
          terminal: true,
        }
      : null,
    teacherToolsEnabled && aiEnabled && state.aiAvailable === true,
    { restoreSaved: teacherToolsEnabled },
  );
  const currentWinRatePoints = timeline.points;
  const currentTimelinePoint = researching
    ? undefined
    : currentWinRatePoints.find((point) => point.moveNumber === moveNumber);
  const currentWinRateBps =
    matchingAnalysis?.result?.winRateBps ?? currentTimelinePoint?.blackWinRateBps;
  const currentScoreHalfPoints =
    matchingAnalysis?.result?.scoreLeadHalfPoints ?? currentTimelinePoint?.blackScoreLeadHalfPoints;
  const savedSnapshot = timeline.data?.savedSnapshot;
  const savedAtLabel = savedSnapshot
    ? savedAnalysisDate.format(new Date(savedSnapshot.savedAt))
    : null;
  const hasSavedResults = savedSnapshot != null && currentWinRatePoints.length > 0;
  const analysisProgress = timeline.error
    ? aiEnabled
      ? '补算暂时中断，已有记录保留'
      : '记录读取失败，请重试'
    : timeline.loading && timeline.data === null
      ? '正在读取已保存分析…'
      : !aiEnabled
        ? hasSavedResults
          ? `已恢复记录 · ${timeline.data!.completed}/${timeline.data!.total}局面`
          : '暂无保存记录'
        : timeline.data
          ? `${timeline.data.completed === timeline.data.total ? '全谱分析已保存' : '全谱补算中'} · ${timeline.data.completed}/${timeline.data.total}局面`
          : '准备全谱分析…';
  const metricCaption = matchingAnalysis?.result
    ? 'AI局面评估'
    : currentTimelinePoint
      ? '已保存评估'
      : aiEnabled
        ? '当前局面分析中'
        : '本局面暂无记录';
  const savedDescription = hasSavedResults
    ? `${savedSnapshot.matchesCurrentConfiguration ? '已恢复保存结果' : '已恢复旧算力记录'} · ${savedAtLabel}（北京时间）`
    : timeline.loading
      ? '正在读取已保存分析…'
      : '开启后分析当前局面，并补全缺失曲线';
  const aiCandidates = useMemo(() => presentAiCandidates(matchingAnalysis), [matchingAnalysis]);
  const visibleAiVariation = matchingAnalysis === null ? null : state.aiVariation;
  const aiVariationLabels = useMemo(
    () => aiVariationStoneLabels(visibleAiVariation),
    [visibleAiVariation],
  );
  const displayedStones = visibleAiVariation?.stones ?? stones;
  const displayedLabels = visibleAiVariation === null ? labels : aiVariationLabels;
  const aiCandidateKeys = useMemo(
    () => new Set(aiCandidates.map((candidate) => pointKey(candidate.point))),
    [aiCandidates],
  );
  const floatingPanels = reviewFloatingPanelLayout(aiEnabled, annotationOpen);
  const boardViewport = useMemo(
    () => (boardCorner === null ? null : boardViewportForCorner(boardCorner)),
    [boardCorner],
  );

  useEffect(() => {
    setMoveNumber(0);
    setMarkerMode('LAST_ONE');
    setResearchBase(null);
    setVariationMoves([]);
    setResearchCursor(0);
    setAnnotations([]);
    setAnnotationOpen(false);
    setGameListOpen(false);
    setBoardZoomMenuOpen(false);
    setBoardCorner(null);
    setAnalyticsTab('WIN_RATE');
    setAiRecommendationsOpen(false);
  }, [review?.record.gameId]);

  useEffect(() => {
    if (!researching && frame !== undefined && matchingAnalysis !== null)
      timeline.recordForeground(frame.moveNumber, matchingAnalysis);
  }, [frame, matchingAnalysis, researching, timeline.recordForeground]);

  useEffect(() => {
    if (captureMessage === null) return;
    const timer = window.setTimeout(() => setCaptureMessage(null), 2_400);
    return () => window.clearTimeout(timer);
  }, [captureMessage]);

  useEffect(() => {
    void store.refreshAiStatus();
    return () => store.cancelAi();
  }, [store]);

  useEffect(() => {
    if (!aiEnabled || review === null || state.aiAvailable !== true) {
      return;
    }
    const timer = window.setTimeout(() => {
      if (teacherToolsEnabled) {
        if (reviewAnalysisPosition !== null)
          void store.analyzeObservation(reviewAnalysisPosition).catch(() => undefined);
      } else {
        void store
          .analyze(
            review.record.gameId,
            researchBase ?? moveNumber,
            researchBase === null ? [] : activeVariationMoves,
          )
          .catch(() => undefined);
      }
    }, 40);
    return () => window.clearTimeout(timer);
  }, [
    activeVariationMoves,
    aiEnabled,
    moveNumber,
    researchBase,
    review,
    state.aiAvailable,
    state.aiSelectedConfigurationId,
    teacherToolsEnabled,
    reviewAnalysisPosition,
    store,
  ]);

  useLayoutEffect(() => {
    const stage = boardStageRef.current;
    if (stage === null) return;
    const fitBoard = () => {
      const bounds = stage.getBoundingClientRect();
      const size = Math.floor(Math.min(bounds.width, bounds.height));
      stage.style.setProperty('--fox-board-size', `${Math.max(0, size)}px`);
    };
    fitBoard();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', fitBoard);
      return () => window.removeEventListener('resize', fitBoard);
    }
    const observer = new ResizeObserver(fitBoard);
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const update = () => {
      setImmersive(document.fullscreenElement === pageRef.current);
    };
    document.addEventListener('fullscreenchange', update);
    return () => document.removeEventListener('fullscreenchange', update);
  }, []);

  const navigateMainline = useCallback(
    (target: number) => {
      store.cancelAi();
      store.clearAiVariation();
      store.clearResearch();
      setResearchBase(null);
      setVariationMoves([]);
      setResearchCursor(0);
      setMoveNumber(Math.max(0, Math.min(totalMoves, Math.trunc(target))));
    },
    [store, totalMoves],
  );

  const closeRecommendations = () => {
    setAiRecommendationsOpen(false);
    store.clearAiVariation();
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setAiRecommendationsOpen(false);
        store.clearAiVariation();
      }
      if (
        event.target instanceof Element &&
        event.target.closest(
          'input, textarea, select, button, [contenteditable="true"], [role="dialog"]',
        )
      )
        return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        if (researching) navigateResearch(Math.max(0, researchCursor - 1));
        else navigateMainline(moveNumber - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        if (researching) navigateResearch(Math.min(variationMoves.length, researchCursor + 1));
        else navigateMainline(moveNumber + 1);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    researchCursor,
    researching,
    moveNumber,
    totalMoves,
    variationMoves.length,
    navigateMainline,
    store,
  ]);

  const toggleFullscreen = async () => {
    if (document.fullscreenElement !== null) {
      await document.exitFullscreen().catch(() => undefined);
      setImmersive(false);
      return;
    }
    if (immersive) {
      setImmersive(false);
      return;
    }
    const page = pageRef.current;
    if (page?.requestFullscreen !== undefined) {
      await page.requestFullscreen().catch(() => setImmersive(true));
      return;
    }
    setImmersive(true);
  };

  const cycleMarker = () => {
    setMarkerMode((current) =>
      current === 'LAST_ONE' ? 'LAST_FIVE' : current === 'LAST_FIVE' ? 'ALL' : 'LAST_ONE',
    );
  };

  const endResearch = () => {
    store.cancelAi();
    store.clearResearch();
    setResearchBase(null);
    setVariationMoves([]);
    setResearchCursor(0);
  };

  const placeResearch = (point: BoardPoint) => {
    if (review === null || researchBase === null || state.researchPending) return;
    const next = branchResearchVariation(variationMoves, researchCursor, point);
    store.cancelAi();
    void store
      .research(review.record.gameId, researchBase, next)
      .then((applied) => {
        if (!applied) return;
        setVariationMoves(next);
        setResearchCursor(next.length);
      })
      .catch(() => undefined);
  };

  const navigateResearch = (cursor: number) => {
    if (review === null || researchBase === null || state.researchPending) return;
    const target = Math.max(0, Math.min(variationMoves.length, Math.trunc(cursor)));
    if (target === researchCursor) return;
    store.cancelAi();
    if (target === 0) {
      store.clearResearch();
      setResearchCursor(0);
      return;
    }
    void store
      .research(review.record.gameId, researchBase, researchVariationPrefix(variationMoves, target))
      .then((applied) => {
        if (applied) setResearchCursor(target);
      })
      .catch(() => undefined);
  };

  useBoardWheelNavigation(boardStageRef, {
    enabled: teacherToolsEnabled,
    blocked:
      review === null ||
      state.pending ||
      state.researchPending ||
      annotationOpen ||
      gameListOpen ||
      aiConnectionOpen,
    canPrevious: researching ? researchCursor > 0 : moveNumber > 0,
    canNext: researching ? researchCursor < variationMoves.length : moveNumber < totalMoves,
    context: `${review?.record.gameId ?? 'empty'}:${researchBase ?? 'main'}`,
    onStep: (direction) => {
      const currentState = store.snapshot();
      if (currentState.pending || currentState.researchPending) return;
      store.clearAiVariation();
      if (aiEnabled) store.cancelAi();
      if (researching) navigateResearch(researchCursor + direction);
      else navigateMainline(moveNumber + direction);
    },
  });

  const toggleResearch = () => {
    if (researching) {
      endResearch();
      return;
    }
    if (review === null) return;
    store.clearAiVariation();
    setResearchBase(moveNumber);
    setVariationMoves([]);
    setResearchCursor(0);
    store.clearResearch();
  };

  const selectAiCandidate = (point: BoardPoint) => {
    if (review === null || state.researchPending || state.aiPending) return;
    if (researching) {
      placeResearch(point);
      return;
    }
    const next = [point];
    store.cancelAi();
    setResearchBase(moveNumber);
    setVariationMoves([]);
    setResearchCursor(0);
    store.clearResearch();
    void store
      .research(review.record.gameId, moveNumber, next)
      .then((applied) => {
        if (!applied) return;
        setVariationMoves(next);
        setResearchCursor(1);
      })
      .catch(() => endResearch());
  };

  const checkAiConnection = async () => {
    if (teacherToolsEnabled && !rulesAvailable) return;
    setAiChecking(true);
    await store.refreshAiStatus();
    setAiChecking(false);
    if (store.snapshot().aiAvailable === true && review !== null) {
      setAiConnectionOpen(false);
      setAiEnabled(true);
    }
  };

  const toggleAi = async () => {
    if (aiEnabled) {
      setAiEnabled(false);
      store.cancelAi();
      return;
    }
    if (teacherToolsEnabled && !rulesAvailable) {
      setAiConnectionOpen(true);
      return;
    }
    if (state.aiAvailable === true && review !== null) {
      setAiEnabled(true);
      return;
    }
    setAiConnectionOpen(true);
    await checkAiConnection();
  };

  const addStroke = (stroke: ReviewBrushStroke) => {
    setAnnotations((current) => [...current, stroke]);
  };

  const addPointAnnotation = (point: BoardPoint) => {
    if (annotationTool === 'BRUSH') return;
    setAnnotations((current) =>
      toggleReviewPointAnnotation(current, point, annotationTool, annotationColor),
    );
  };

  const openGameList = () => {
    setGameListOpen(true);
    void (teacherToolsEnabled ? onRefreshTeacherGames() : onRefreshPlayerGames()).catch(
      () => undefined,
    );
  };

  const selectGame = (gameId: string) => {
    store.cancelAi();
    store.clearResearch();
    setAiEnabled(false);
    setResearchBase(null);
    setVariationMoves([]);
    setResearchCursor(0);
    setAnnotationOpen(false);
    setAnnotations([]);
    setGameListOpen(false);
    setBoardZoomMenuOpen(false);
    setBoardCorner(null);
    void onSelectGame(gameId).catch(() => undefined);
  };

  const captureBoard = () => {
    const svg = boardStageRef.current?.querySelector<SVGSVGElement>('svg.go-board');
    if (svg === undefined || svg === null) {
      setCaptureMessage('当前棋盘暂时无法截图。');
      return;
    }
    const gameId = review?.record.gameId ?? 'my-game';
    const annotationSvg = boardStageRef.current?.querySelector<SVGSVGElement>(
      'svg.review-annotation-layer',
    );
    void downloadBoardJpeg(svg, foxBoardCaptureFilename(gameId, moveNumber), annotationSvg)
      .then(() => setCaptureMessage('当前棋盘已保存为 JPG 图片。'))
      .catch((error: unknown) =>
        setCaptureMessage(userErrorMessage(error, '棋盘截图失败，请重试。')),
      );
  };

  const closeAnnotationPanel = () => {
    setAnnotationOpen(false);
  };

  const toggleAnnotationPanel = () => {
    if (annotationOpen) {
      closeAnnotationPanel();
      return;
    }
    setAnnotationOpen(true);
  };

  const annotationPanel = floatingPanels.showAnnotationPanel ? (
    <ClassroomAnnotationToolbar
      tool={annotationTool}
      color={annotationColor}
      width={annotationWidth}
      annotationCount={annotations.length}
      sharingStage={floatingPanels.annotationSharesStage}
      onTool={setAnnotationTool}
      onColor={setAnnotationColor}
      onWidth={setAnnotationWidth}
      onUndo={() => setAnnotations((current) => current.slice(0, -1))}
      onClear={() => setAnnotations([])}
      onClose={closeAnnotationPanel}
    />
  ) : null;

  const reviewTools = (
    <div className="fox-kifu-tool-buttons" role="group" aria-label="棋谱分析工具">
      <button
        type="button"
        disabled={review === null || (!teacherToolsEnabled && researching)}
        aria-label={teacherToolsEnabled ? `手数标记：${MARKER_LABELS[markerMode]}` : undefined}
        title={teacherToolsEnabled ? '切换手数标记' : undefined}
        onClick={cycleMarker}
      >
        <TargetIcon aria-hidden="true" />
        <span>
          {teacherToolsEnabled ? '' : '标记：'}
          {MARKER_LABELS[markerMode]}
        </span>
      </button>
      {teacherToolsEnabled ? (
        <>
          <button
            type="button"
            className={annotationOpen ? 'is-active' : ''}
            disabled={review === null}
            onClick={toggleAnnotationPanel}
          >
            <Pencil2Icon aria-hidden="true" />
            标记
          </button>
          <button
            type="button"
            className={`game-review-research-button${researching ? ' is-active' : ''}${researching && state.researchPending ? ' is-research-busy' : ''}`}
            disabled={review === null || state.researchPending || annotationOpen}
            onClick={toggleResearch}
          >
            <ResetIcon aria-hidden="true" />
            {researching ? '结束试下' : '研究'}
          </button>
          <button type="button" disabled={teacherGameListPending} onClick={openGameList}>
            <ListBulletIcon aria-hidden="true" />
            对局列表
          </button>
          <button
            type="button"
            className={boardCorner !== null ? 'is-active' : ''}
            disabled={review === null}
            aria-label={boardCorner === null ? '选择棋盘放大区域' : '恢复全盘'}
            onClick={() => {
              if (reviewBoardZoomButtonAction(boardCorner) === 'RESTORE_FULL_BOARD') {
                setBoardCorner(null);
                setBoardZoomMenuOpen(false);
                return;
              }
              setBoardZoomMenuOpen((open) => !open);
            }}
          >
            {boardCorner === null ? (
              <ZoomInIcon aria-hidden="true" />
            ) : (
              <ZoomOutIcon aria-hidden="true" />
            )}
            {boardCorner === null ? '放大' : '还原'}
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className={`${aiEnabled ? 'is-active' : ''}${state.aiPending ? ' is-loading' : ''}`}
            onClick={() => void toggleAi()}
          >
            <LightningBoltIcon aria-hidden="true" />
            {state.aiPending ? '连接 / 分析中…' : aiEnabled ? '暂停AI' : 'AI算力'}
          </button>
          <button
            type="button"
            className={`game-review-research-button${researching ? ' is-active' : ''}${researching && state.researchPending ? ' is-research-busy' : ''}`}
            disabled={review === null || state.researchPending || annotationOpen}
            onClick={toggleResearch}
          >
            <ResetIcon aria-hidden="true" />
            {researching ? '结束试下' : '对局研究'}
          </button>
          <button
            type="button"
            className={annotationOpen ? 'is-active' : ''}
            disabled={review === null}
            onClick={toggleAnnotationPanel}
          >
            <Pencil2Icon aria-hidden="true" />
            添加标记
          </button>
          <button type="button" disabled={playerGameListPending} onClick={openGameList}>
            <ListBulletIcon aria-hidden="true" />
            对局列表
          </button>
          <button type="button" disabled={review === null} onClick={captureBoard}>
            <CameraIcon aria-hidden="true" />
            截图
          </button>
        </>
      )}
      {aiEnabled && !teacherToolsEnabled ? (
        <span className="fox-kifu-ai-active-state">持续分析中</span>
      ) : null}
    </div>
  );

  const reviewToolbar = teacherToolsEnabled ? (
    <div className="teacher-review-controls">
      {reviewTools}
      {aiEnabled && state.aiError !== null ? (
        <p className="review-status-error" role="alert">
          {state.aiError}
        </p>
      ) : null}
      {researching ? (
        <p className="review-research-status" role="status">
          试下 · 变化第 {researchCursor} 手（原谱第 {researchBase} 手）
        </p>
      ) : null}
      <HomeworkMoveControls
        value={researching ? researchCursor : moveNumber}
        max={researching ? variationMoves.length : totalMoves}
        disabled={review === null || state.pending}
        busy={researching && state.researchPending}
        onChange={researching ? navigateResearch : navigateMainline}
      />
    </div>
  ) : (
    <div className="student-review-controls">
      {reviewTools}
      <div className="student-review-navigation" role="group" aria-label="棋谱前进与后退">
        <button
          className={researching && state.researchPending ? 'is-research-busy' : ''}
          type="button"
          disabled={researching ? researchCursor === 0 : moveNumber === 0}
          onClick={() => (researching ? navigateResearch(0) : navigateMainline(0))}
        >
          <DoubleArrowLeftIcon aria-hidden="true" />
          {researching ? '变化起点' : '首手'}
        </button>
        <button
          className={researching && state.researchPending ? 'is-research-busy' : ''}
          type="button"
          disabled={researching ? researchCursor === 0 || state.researchPending : moveNumber === 0}
          onClick={() =>
            researching ? navigateResearch(researchCursor - 1) : navigateMainline(moveNumber - 1)
          }
        >
          <ArrowLeftIcon aria-hidden="true" />
          {researching ? '后退' : '上一手'}
        </button>
        <span className="student-review-move-status">
          <strong>
            {researching ? `变化第 ${researchCursor} 手` : `第 ${moveNumber} / ${totalMoves} 手`}
          </strong>
          <small>{review?.record.resultText ?? '—'}</small>
        </span>
        <button
          className={researching && state.researchPending ? 'is-research-busy' : ''}
          type="button"
          disabled={
            researching
              ? researchCursor >= variationMoves.length || state.researchPending
              : frames.length === 0 || moveNumber >= totalMoves
          }
          onClick={() =>
            researching ? navigateResearch(researchCursor + 1) : navigateMainline(moveNumber + 1)
          }
        >
          {researching ? '前进' : '下一手'}
          <ArrowRightIcon aria-hidden="true" />
        </button>
        <button
          className={researching && state.researchPending ? 'is-research-busy' : ''}
          type="button"
          disabled={
            researching
              ? researchCursor >= variationMoves.length || state.researchPending
              : frames.length === 0 || moveNumber >= totalMoves
          }
          onClick={() =>
            researching ? navigateResearch(variationMoves.length) : navigateMainline(totalMoves)
          }
        >
          {researching ? '变化末手' : '末手'}
          <DoubleArrowRightIcon aria-hidden="true" />
        </button>
      </div>
      {aiEnabled && state.aiError !== null ? (
        <button className="fox-kifu-ai-error" type="button" onClick={() => store.clearAiError()}>
          {state.aiError}
        </button>
      ) : null}
    </div>
  );

  return (
    <main
      ref={pageRef}
      className={`board-workspace fox-kifu-page game-history-review-page game-history-review-page--sidebar-tools game-review-studio${teacherToolsEnabled ? ' teacher-review-studio' : ' student-review-studio'}${immersive ? ' fox-kifu-page--immersive' : ''}`}
    >
      <div className="fox-kifu-workspace">
        {!teacherToolsEnabled ? (
          <aside className="fox-kifu-move-rail" aria-label={`${title}手数导航`}>
            <button
              className="back-button fox-kifu-back return-button"
              type="button"
              onClick={onBack}
            >
              ← 返回大厅
            </button>
            {(['BLACK', 'WHITE'] as const).map((color) => (
              <div className="fox-kifu-player-badge" key={color}>
                <span className={`stone-preview stone-preview--${color.toLowerCase()}`} />
                <strong>
                  {color === 'BLACK'
                    ? (review?.record.blackName ?? '黑方')
                    : (review?.record.whiteName ?? '白方')}
                </strong>
                <small>{color === 'BLACK' ? '黑棋' : '白棋'}</small>
              </div>
            ))}
            <div className="fox-kifu-vertical-progress">
              <strong>{researching ? researchCursor : moveNumber}</strong>
              <VerticalMoveSlider
                max={researching ? variationMoves.length : totalMoves}
                value={researching ? researchCursor : moveNumber}
                busy={researching && state.researchPending}
                disabled={
                  frames.length === 0 ||
                  (researching && (variationMoves.length === 0 || state.researchPending))
                }
                onChange={researching ? navigateResearch : navigateMainline}
              />
              <small>/ {researching ? variationMoves.length : totalMoves}</small>
            </div>
            <BoardMoveProgress
              value={researching ? researchCursor : moveNumber}
              max={researching ? variationMoves.length : totalMoves}
              disabled={
                frames.length === 0 ||
                (researching && (variationMoves.length === 0 || state.researchPending))
              }
              onChange={researching ? navigateResearch : navigateMainline}
            />
          </aside>
        ) : null}

        <section className="fox-kifu-board-panel">
          <div ref={boardStageRef} className="fox-kifu-board-stage">
            <div className="teacher-board-frame">
              <BoardSurface
                stoneAppearance="shell"
                coordinateEdges={{ top: true, left: true }}
                size={boardSize}
                stones={displayedStones}
                lastMove={
                  visibleAiVariation === null && labels.length === 0 && frame?.kind === 'PLAY'
                    ? frame.point
                    : null
                }
                stoneLabels={displayedLabels}
                aiCandidates={
                  visibleAiVariation === null && (!teacherToolsEnabled || aiRecommendationsOpen)
                    ? aiCandidates
                    : []
                }
                viewport={boardViewport}
                annotationLayer={
                  <ReviewAnnotationLayer
                    size={boardSize}
                    open={annotationOpen}
                    tool={annotationTool}
                    color={annotationColor}
                    width={annotationWidth}
                    annotations={annotations}
                    viewport={boardViewport}
                    onStroke={addStroke}
                    onPoint={addPointAnnotation}
                  />
                }
                {...(annotationOpen || visibleAiVariation !== null
                  ? {}
                  : researching
                    ? {
                        isActionable: (_point: BoardPoint, stone: StoneColor | null) =>
                          stone === null && !state.researchPending,
                        onPoint: placeResearch,
                      }
                    : aiEnabled &&
                        (!teacherToolsEnabled || aiRecommendationsOpen) &&
                        aiCandidates.length > 0
                      ? {
                          isActionable: (point: BoardPoint, stone: StoneColor | null) =>
                            stone === null && aiCandidateKeys.has(pointKey(point)),
                          onPoint: selectAiCandidate,
                        }
                      : {})}
              />
            </div>
            {review === null ? (
              <div className="fox-kifu-board-empty">
                <strong>{state.pending ? `正在加载${title}…` : '暂时无法显示棋谱'}</strong>
                <span>{state.error ?? '请返回列表后重新选择。'}</span>
              </div>
            ) : null}
            {captureMessage !== null ? (
              <div className="fox-kifu-capture-toast" role="status">
                {captureMessage}
              </div>
            ) : null}
            {aiConnectionOpen ? (
              <section
                className="fox-kifu-details-backdrop game-review-ai-backdrop"
                role="presentation"
                onMouseDown={(event) => {
                  if (event.currentTarget === event.target) setAiConnectionOpen(false);
                }}
              >
                <div
                  className="fox-kifu-ai-dialog"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="game-review-ai-dialog-title"
                >
                  <header>
                    <span>
                      <small>CLOUD AI COMPUTE</small>
                      <h2 id="game-review-ai-dialog-title">云AI算力</h2>
                    </span>
                    <button type="button" onClick={() => setAiConnectionOpen(false)}>
                      关闭
                    </button>
                  </header>
                  <div className="fox-kifu-ai-connection-status" aria-live="polite">
                    <i className={state.aiAvailable === true ? 'is-ready' : ''} />
                    <span>
                      <strong>
                        {aiChecking
                          ? '正在检测连接…'
                          : state.aiAvailable === true
                            ? '算力任务服务可用'
                            : state.aiConfigured === false
                              ? '教师尚未授权云算力'
                              : state.aiAuthorized === false
                                ? '教师尚未授权云算力'
                                : '云算力暂时离线'}
                      </strong>
                    </span>
                  </div>
                  <AiAccountSelector
                    state={state}
                    onSelect={(id) => store.selectAiAccount(id)}
                    onSaveDefault={() => store.saveAiDefault()}
                  />
                  {teacherToolsEnabled ? (
                    <section className="review-ai-rules" aria-label="本局AI分析规则">
                      <strong>本局分析依据</strong>
                      <p>
                        {rulesAvailable
                          ? `${review!.record.handicap === 0 ? '分先' : review!.record.handicap === 1 ? '让先' : `让${review!.record.handicap}子`} · 中国规则 · 白补偿 ${review!.record.komi} 目`
                          : '本局棋份信息暂不可用，请重新加载棋谱后再分析。'}
                      </p>
                      <small>以本局服务端记录为准，全谱与当前局面使用相同棋份。</small>
                    </section>
                  ) : null}
                  {teacherToolsEnabled && savedSnapshot ? (
                    <section className="review-ai-rules" aria-label="已保存分析记录">
                      <strong>已保存分析记录</strong>
                      <p>
                        {savedAtLabel}（北京时间） · {timeline.data!.completed}/
                        {timeline.data!.total} 局面
                      </p>
                      <p>算力配置版本 {savedSnapshot.configurationVersion}</p>
                      <small>
                        {savedSnapshot.matchesCurrentConfiguration
                          ? '记录与当前算力配置一致。重新开启 AI 时只补全缺失曲线；当前局面可继续分析。'
                          : '记录来自先前的算力配置。开启 AI 将使用当前配置，旧记录仍会保留。'}
                      </small>
                    </section>
                  ) : null}
                  {state.aiProfile !== null ? (
                    <dl className="fox-kifu-ai-profile">
                      <div>
                        <dt>算力模式</dt>
                        <dd>{ZHIZI_COMPUTE_MODE_LABELS[state.aiProfile.computeMode]}</dd>
                      </div>
                      <div>
                        <dt>档位 / 权重</dt>
                        <dd>
                          {state.aiProfile.exclusiveTier === null
                            ? '共享档'
                            : ZHIZI_EXCLUSIVE_TIER_LABELS[state.aiProfile.exclusiveTier]}{' '}
                          · {zhiziWeightLabel(state.aiProfile.engineWeight)}
                        </dd>
                      </div>
                      <div>
                        <dt>分析参数</dt>
                        <dd>
                          {state.aiProfile.maxVisits === 0
                            ? '不封顶'
                            : `${state.aiProfile.maxVisits} 计算量`}{' '}
                          · {state.aiProfile.recommendationCount} 个推荐点
                        </dd>
                      </div>
                    </dl>
                  ) : null}
                  <footer>
                    <button
                      type="button"
                      disabled={aiChecking}
                      onClick={() => void checkAiConnection()}
                    >
                      {aiChecking ? '检测中…' : '重新检测并连接'}
                    </button>
                  </footer>
                </div>
              </section>
            ) : null}
            {teacherToolsEnabled && gameListOpen ? (
              <TeacherGamePickerDialog
                games={teacherGames}
                currentGameId={review?.record.gameId ?? null}
                title={teacherGameListTitle}
                scope={teacherGameListScope}
                pending={teacherGameListPending}
                error={teacherGameListError}
                onClose={() => setGameListOpen(false)}
                onRefresh={() => void onRefreshTeacherGames().catch(() => undefined)}
                onSelect={selectGame}
              />
            ) : null}
            {!teacherToolsEnabled && gameListOpen ? (
              <PlayerGamePickerDialog
                games={playerGames}
                currentGameId={review?.record.gameId ?? null}
                pending={playerGameListPending}
                error={playerGameListError}
                onClose={() => setGameListOpen(false)}
                onRefresh={() => void onRefreshPlayerGames().catch(() => undefined)}
                onSelect={selectGame}
              />
            ) : null}
            {teacherToolsEnabled && boardZoomMenuOpen ? (
              <section
                className="review-board-zoom-menu"
                role="dialog"
                aria-label="选择棋盘放大区域"
              >
                <header>
                  <strong>选择放大区域</strong>
                  <button type="button" onClick={() => setBoardZoomMenuOpen(false)}>
                    ×
                  </button>
                </header>
                <div>
                  {(Object.keys(BOARD_CORNER_LABELS) as HomeworkBoardCorner[]).map((corner) => (
                    <button
                      key={corner}
                      type="button"
                      className={boardCorner === corner ? 'is-active' : ''}
                      onClick={() => {
                        setBoardCorner(corner);
                        setBoardZoomMenuOpen(false);
                      }}
                    >
                      {BOARD_CORNER_LABELS[corner]}
                    </button>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </section>

        <aside className="fox-kifu-data-panel game-review-data-panel">
          <header className="fox-kifu-data-title">
            {teacherToolsEnabled ? (
              <button type="button" className="review-back" onClick={onBack}>
                <ArrowLeftIcon aria-hidden="true" />
                返回
              </button>
            ) : null}
            <h1>{title}</h1>
            <button
              className="secondary-button fullscreen-action"
              type="button"
              aria-label={immersive ? '退出全屏' : '棋盘全屏'}
              title={immersive ? '退出全屏' : '棋盘全屏'}
              onClick={() => void toggleFullscreen()}
            >
              {immersive ? (
                <ExitFullScreenIcon aria-hidden="true" />
              ) : (
                <EnterFullScreenIcon aria-hidden="true" />
              )}
              {!teacherToolsEnabled ? (immersive ? '退出全屏' : '棋盘全屏') : null}
            </button>
            {annotationPanel}
          </header>

          {teacherToolsEnabled ? (
            <section className="review-players" aria-label="对局双方剩余时间">
              {(['BLACK', 'WHITE'] as const).map((color) => (
                <div key={color} className="review-player">
                  <ReviewPlayerStone color={color} />
                  <span className="review-player-details">
                    <strong>
                      {color === 'BLACK' ? '黑方' : '白方'} ·{' '}
                      {color === 'BLACK'
                        ? (review?.record.blackName ?? '黑方')
                        : (review?.record.whiteName ?? '白方')}
                    </strong>
                    <small>剩余时间</small>
                    <time
                      className={
                        formatReviewFrameRemainingTime(frame, color, review?.record).length > 6
                          ? 'is-clock-detail'
                          : undefined
                      }
                    >
                      {formatReviewFrameRemainingTime(frame, color, review?.record)}
                    </time>
                  </span>
                </div>
              ))}
              <p className="review-clock-caption">第 {moveNumber} 手后的剩余时间</p>
            </section>
          ) : null}
          {state.error !== null ? (
            <p className="review-status-error" role="alert">
              {state.error}
            </p>
          ) : null}
          {teacherToolsEnabled ? (
            <>
              <section className="review-ai-bar" aria-label="AI算力">
                <button
                  className="review-ai-toggle"
                  type="button"
                  aria-pressed={aiEnabled}
                  onClick={() => void toggleAi()}
                >
                  <span className="review-ai-symbol">
                    <LightningBoltIcon aria-hidden="true" />
                  </span>
                  <span>
                    <strong>AI算力 · {aiEnabled ? '已开启' : '未开启'}</strong>
                    <small>
                      {researching
                        ? '研究变化分析 · 原谱趋势保留'
                        : !aiEnabled
                          ? savedDescription
                          : '基于本局棋谱进行AI分析（当前局面分析）'}
                    </small>
                  </span>
                </button>
                <button
                  type="button"
                  className="review-ai-settings"
                  onClick={() => setAiConnectionOpen(true)}
                >
                  <GearIcon aria-hidden="true" />
                  设置
                </button>
              </section>
              <section className="review-position-metrics" aria-label="当前局面数据">
                <div>
                  <span>黑胜率</span>
                  <strong>
                    {currentWinRateBps !== undefined
                      ? `${(currentWinRateBps / 100).toFixed(1)}%`
                      : '—'}
                  </strong>
                  <small>{metricCaption}</small>
                </div>
                <div>
                  <span>{(currentScoreHalfPoints ?? 0) < 0 ? '白领先' : '黑领先'}</span>
                  <strong>
                    {currentScoreHalfPoints !== undefined ? (
                      <>
                        {(Math.abs(currentScoreHalfPoints) / 2).toFixed(1)}
                        <em>目</em>
                      </>
                    ) : (
                      '—'
                    )}
                  </strong>
                  <small>{metricCaption}</small>
                </div>
                <div className="review-move-duration">
                  <span>
                    {frame?.color === 'BLACK' ? '黑棋' : frame?.color === 'WHITE' ? '白棋' : '开局'}
                    第 {moveNumber} 手用时
                  </span>
                  <strong>{formatPreciseDuration(visibleTiming.currentMoveMs)}</strong>
                </div>
                <button
                  type="button"
                  className="review-recommendations-toggle"
                  aria-expanded={aiRecommendationsOpen}
                  disabled={!aiEnabled}
                  onClick={() =>
                    aiRecommendationsOpen ? closeRecommendations() : setAiRecommendationsOpen(true)
                  }
                >
                  <MagicWandIcon aria-hidden="true" />
                  <span>
                    AI推荐点
                    <ArrowRightIcon aria-hidden="true" />
                  </span>
                </button>
              </section>
            </>
          ) : null}
          <div className="workspace-tool-dock" aria-label="棋盘工具区">
            {floatingPanels.showAiPanel && (!teacherToolsEnabled || aiRecommendationsOpen) ? (
              <div className="review-candidates-popover">
                <AiCandidatePanel
                  {...(teacherToolsEnabled ? { onClose: closeRecommendations } : {})}
                  preserveWidth
                  account={matchingAnalysis?.account ?? selectedAccount(state)}
                  onChangeAccount={() => setAiConnectionOpen(true)}
                  candidates={aiCandidates}
                  selectedRank={visibleAiVariation?.candidateRank ?? null}
                  variationPending={state.aiVariationPending}
                  sharingStage={floatingPanels.annotationSharesStage}
                  onSelect={(rank) => {
                    if (visibleAiVariation?.candidateRank === rank) {
                      store.clearAiVariation();
                      return;
                    }
                    const analysisId = matchingAnalysis?.analysisId;
                    if (analysisId === undefined) return;
                    void store.loadAiVariation(analysisId, rank).catch(() => undefined);
                  }}
                />
              </div>
            ) : null}
          </div>
          {teacherToolsEnabled ? (
            <section className="review-analytics-panel" aria-label="对局分析">
              <div className="review-analytics-tabs" role="tablist" aria-label="分析视图">
                <button
                  type="button"
                  role="tab"
                  aria-selected={analyticsTab === 'TIMING'}
                  className={analyticsTab === 'TIMING' ? 'is-active' : ''}
                  onClick={() => setAnalyticsTab('TIMING')}
                >
                  最近逐手用时
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={analyticsTab === 'WIN_RATE'}
                  className={analyticsTab === 'WIN_RATE' ? 'is-active' : ''}
                  onClick={() => setAnalyticsTab('WIN_RATE')}
                >
                  胜率走势图
                </button>
              </div>
              {analyticsTab === 'TIMING' ? (
                <section className="review-history-timing-card" aria-label="最近逐手用时">
                  <header>
                    <strong>最近逐手用时</strong>
                    <span>截至第 {moveNumber} 手</span>
                  </header>
                  <RecentReviewTimingChart frames={recentTimedMoves} moveNumber={moveNumber} />
                  <p>点击下方进度条，查看不同阶段的思考用时</p>
                </section>
              ) : (
                <div className="review-trend-card">
                  <TeacherObservationTrendChart
                    points={currentWinRatePoints}
                    currentMove={moveNumber}
                    lastMoveNumber={totalMoves}
                    onSelectMove={navigateMainline}
                    aiEnabled={aiEnabled}
                    aiPending={state.aiPending}
                    historyLayout
                    progressLabel={analysisProgress}
                    {...(!aiEnabled
                      ? {
                          emptyLabel: timeline.error
                            ? '已保存分析读取失败，请点击重试'
                            : timeline.loading
                              ? '正在读取已保存分析…'
                              : '暂无保存记录，开启 AI 后自动分析并保存',
                        }
                      : {})}
                    {...(timeline.error ? { onRetry: timeline.retry } : {})}
                  />
                </div>
              )}
            </section>
          ) : (
            <ReviewTiming
              frame={frame}
              moveNumber={moveNumber}
              visibleTiming={visibleTiming}
              fullTiming={fullTiming}
              recentMoves={recentTimedMoves}
              emptyTotalsAsZero
              reportPending={reportPending}
              reportDisabled={review === null || review.record.timingRuleCode !== 'ABSOLUTE_V1'}
            />
          )}
          {reviewToolbar}
        </aside>
      </div>
    </main>
  );
}
